import { create } from 'zustand';
import { api } from '@/lib/api';
import type { ApiResponse } from '@nexus/shared/types';

export interface TeamMember {
  id: string;
  email: string;
  name: string;
  role: string;
  avatarUrl?: string | null;
  lastLoginAt: number | null;
  createdAt: number;
}

interface InviteData {
  email: string;
  name: string;
  role?: 'admin' | 'member';
}

interface InviteResult {
  user: TeamMember;
  tempPassword: string;
}

interface UsersState {
  members: TeamMember[];
  isLoading: boolean;
  error: string | null;
  fetchTeam: () => Promise<void>;
  invite: (data: InviteData) => Promise<InviteResult>;
  updateRole: (id: string, role: 'admin' | 'member') => Promise<void>;
  remove: (id: string) => Promise<void>;
  clearError: () => void;
}

export const useUsersStore = create<UsersState>((set, get) => ({
  members: [],
  isLoading: false,
  error: null,

  fetchTeam: async () => {
    set({ isLoading: true, error: null });
    try {
      const res = await api.get<ApiResponse<TeamMember[]>>('/api/users');
      set({ members: res.data ?? [], isLoading: false });
    } catch (err) {
      set({ isLoading: false, error: (err as Error).message || 'Failed to load team' });
    }
  },

  invite: async (data: InviteData) => {
    const res = await api.post<ApiResponse<InviteResult>>('/api/users/invite', data);
    if (!res.data) throw new Error('No data returned');
    // Refresh team list
    const teamRes = await api.get<ApiResponse<TeamMember[]>>('/api/users');
    set({ members: teamRes.data ?? [] });
    return res.data;
  },

  updateRole: async (id: string, role: 'admin' | 'member') => {
    await api.patch<ApiResponse<{ id: string; role: string }>>(`/api/users/${id}/role`, { role });
    // Update locally
    set({
      members: get().members.map((m) => (m.id === id ? { ...m, role } : m)),
    });
  },

  remove: async (id: string) => {
    await api.del(`/api/users/${id}`);
    set({ members: get().members.filter((m) => m.id !== id) });
  },

  clearError: () => set({ error: null }),
}));
